import { useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import * as proveAuth from '@prove-identity/prove-auth';
import { proveVerificationStart, proveVerificationValidate, proveVerificationComplete } from 'store/TradeInState';
import { mapIsMobileToFlowtype } from 'pages/tradeIn/TradeInUtils';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { RootState } from 'store/store';

interface OtpActions {
  submitOtp?: (otp: string) => void;
  cancelOtpFinish?: () => void;
  resendOtp?: () => void;
}

const useProveOtpNew = () => {
  const ERROR_CODES = {
    VERIFICATION_FAILED: 'VERIFICATION_FAILED',
    OTP_AUTHENTICATION_FAILED: 'OTP_AUTHENTICATION_FAILED',
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    OTP_INVALID: 'OTP_INVALID',
    OTP_RESEND: 'OTP_RESEND',
    OTP_CANCEL: 'OTP_CANCEL',
    AUTH_TOKEN_MISSING: 'AUTH_TOKEN_MISSING',
  };

  // const dispatch = useDispatch();
  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();
  const authRef = useRef<any>(null);
  const otpPromiseRef = useRef<{
    resolve: (result: proveAuth.OtpFinishResult) => void;
    reject: (error: any) => void;
  } | null>(null);

  const [otpActions, setOtpActions] = useState<OtpActions>({});
  const [otpError, setOtpError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);

  const hasStartedRef = useRef(false);

  const startProveVerification = useCallback(
    async (isMobile: boolean, leadData: any, phoneNumber: string, onNext?: () => void): Promise<void> => {
      if (hasStartedRef.current) return;
      hasStartedRef.current = true;
      try {
        setValidationError(null);
        setOtpError(null);
        setOtpSent(false); // Reset the flag
        const res = await dispatch(
          proveVerificationStart({
            data: {
              phoneNumber: phoneNumber,
              leadId: leadData?.Lead?._id,
              flowType: mapIsMobileToFlowtype(isMobile),
            },
            locale: 'en',
          })
        );

        const authToken = res?.payload?.data?.authToken;
        if (!authToken) {
          hasStartedRef.current = false;
          throw new Error(ERROR_CODES.AUTH_TOKEN_MISSING);
        }

        await sendOTP(authToken, phoneNumber, onNext);
      } catch (error: any) {
        hasStartedRef.current = false;
        console.error('startProveVerification error:', error);
        setValidationError(error.message || ERROR_CODES.VERIFICATION_FAILED);
        throw error;
      }
    },
    []
  );

  const sendOTP = useCallback(async (authToken: string, phoneNumber: string, onNext?: () => void) => {
    try {
      setOtpSent(true);
      hasStartedRef.current = false;

      const builder = new proveAuth.AuthenticatorBuilder()
        .withAuthFinishStep((input) => authFinishStep(input, onNext))
        .withOtpFallback(
          (phoneNumberNeeded, phoneValidationError) =>
            otpStartStep(phoneNumberNeeded, phoneValidationError, phoneNumber),
          otpFinishStep
        );

      const authenticator = builder.build();
      authRef.current = authenticator;
      await authenticator.authenticate(authToken);
    } catch (e: any) {
      hasStartedRef.current = false;
      console.error('sendOTP error:', e);
      setValidationError(e.message || ERROR_CODES.OTP_AUTHENTICATION_FAILED);
    }
  }, []);

  const otpStartStep: proveAuth.OtpStartStepFn = async (
    phoneNumberNeeded: boolean,
    phoneValidationError?: proveAuth.PhoneValidationError,
    phoneNumber?: string
  ): Promise<proveAuth.OtpStartInput | null> => {
    if (phoneNumberNeeded) {
      if (phoneValidationError) {
        throw new Error(`Validation Error: ${phoneValidationError.message}`);
      }
      // const phoneNumber =  getValues('confirmMobile') || getValues('phone');
      return { phoneNumber: phoneNumber ?? '' };
    }
    return null;
  };

  const otpFinishStep: proveAuth.OtpFinishStepFn = async (
    otpError: proveAuth.OtpError | undefined
  ): Promise<proveAuth.OtpFinishResult> => {
    return new Promise<proveAuth.OtpFinishResult>((resolve, reject) => {
      otpPromiseRef.current = { resolve, reject };
      setOtpError(otpError ? 'OTP is invalid. Try re-entering the OTP' : null);

      if (otpError) {
        hasStartedRef.current = false;
      }

      const submitOtp = (otp: string) => {
        if (otpPromiseRef.current) {
          otpPromiseRef.current.resolve({
            input: { otp },
            resultType: proveAuth.OtpFinishResultType.OnSuccess,
          });
          otpPromiseRef.current = null;
        }
      };

      const cancelOtpFinish = () => {
        if (otpPromiseRef.current) {
          otpPromiseRef.current.reject(new Error('User has canceled OTP flow'));
          otpPromiseRef.current = null;
          setOtpSent(false); // Reset the flag
        }
      };

      const resendOtp = () => {
        if (otpPromiseRef.current) {
          otpPromiseRef.current.resolve({
            resultType: proveAuth.OtpFinishResultType.OnResendOtp,
          });
          otpPromiseRef.current = null;
        }
      };

      setOtpActions({ submitOtp, cancelOtpFinish, resendOtp });
    });
  };

  const authFinishStep: proveAuth.AuthFinishStepFn = async (
    input: proveAuth.AuthFinishStepInput,

    onNext?: () => void
  ): Promise<any> => {
    if (input?.authId) {
      await validateUser(input.authId, onNext);
    }
  };

  const validateUser = async (correlationId: string, onNext?: (data: any) => void) => {
    try {
      const response = await dispatch(
        proveVerificationValidate({
          data: { correlationId },
        })
      );

      const { success } = response.payload?.data?.validateResponse || {};
      const leadData = response.payload?.data?.leadData;

      if (success && leadData?._id) {
        await completeProveVerification(leadData, correlationId, onNext);
      } else {
        hasStartedRef.current = false; // Reset for retry
        setValidationError(ERROR_CODES.VALIDATION_ERROR);

        setOtpError(ERROR_CODES.OTP_INVALID);
        // Reset to allow retry
        // setOtpActions({});
      }
    } catch (error: any) {
      hasStartedRef.current = false;
      console.error('validateUser error:', error);
      setValidationError(error.message || ERROR_CODES.VALIDATION_ERROR);
      setOtpError('Something went wrong. Try again.');
      setOtpActions({});
    }
  };

  const completeProveVerification = async (leadData: any, correlationId: string, onNext?: (data: any) => void) => {
    try {
      const response = await dispatch(
        proveVerificationComplete({
          data: {
            leadId: leadData?._id,
            correlationId,
          },
        })
      );

      setOtpSent(false); // Reset the flag
      onNext?.(response);
    } catch (error: any) {
      hasStartedRef.current = false;
      console.error('completeProveVerification error:', error);
      setValidationError(error.message || ERROR_CODES.VERIFICATION_FAILED);
    }
  };

  return {
    startProveVerification,
    otpActions,
    otpError,
    validationError,
    otpSent,
    ERROR_CODES,
    sendOTP,
  };
};

export default useProveOtpNew;
